import { useEffect, useState } from "react";
import i18n, { detectBrowserLang } from "../i18n";
import {
  getPlatformAdapter,
  initializePlatform,
  type HostPlatform,
  type HostUser,
  type PlatformCapabilities,
} from "./platform";

type PlatformSnapshot = {
  platform: HostPlatform;
  isEmbedded: boolean;
  hasNativeBackButton: boolean;
  capabilities: PlatformCapabilities;
  startParam: string | null;
  user: HostUser | null;
  language: string | null;
};

let initializationPromise: ReturnType<typeof initializePlatform> | null = null;

function ensurePlatformInitialized() {
  initializationPromise ??= initializePlatform();
  return initializationPromise;
}

function snapshot(): PlatformSnapshot {
  const adapter = getPlatformAdapter();
  return {
    platform: adapter.platform,
    isEmbedded: adapter.isEmbedded,
    hasNativeBackButton: adapter.hasNativeBackButton,
    capabilities: adapter.capabilities,
    startParam: adapter.getStartParam(),
    user: adapter.getUser(),
    language: adapter.getLanguage(),
  };
}

/** Resolve host SDK hints, geometry, and the locale chunk before React can
 * paint host/auth chrome. The mounted hook below then owns live viewport
 * listeners for the rest of the session. Host initialization failure is not
 * allowed to break Lexibit's local-only fallback. */
export async function mountAfterInitialPlatformPaint(mount: () => void): Promise<void> {
  let hostInitialized = true;
  const adapter = await ensurePlatformInitialized().catch(() => {
    hostInitialized = false;
    return getPlatformAdapter();
  });

  const stopInitialViewportSync = adapter.syncViewportCssVars();
  stopInitialViewportSync();
  // A host SDK that never finished loading (slow or blocked network) answers
  // with its own neutral default rather than the device language, which would
  // pin the first paint to English on a phone that is not English. Its answer
  // is only trusted once initialization actually succeeded.
  const language = detectBrowserLang(hostInitialized ? adapter.getLanguage() : undefined);
  await i18n.changeLanguage(language);
  document.documentElement.lang = language;
  document.documentElement.dir = i18n.dir(language);
  mount();
}

/** Initializes the active host and keeps host viewport/safe-area CSS vars fresh. */
export function usePlatformReadyAndExpand(): PlatformSnapshot {
  const [state, setState] = useState(snapshot);

  useEffect(() => {
    let cancelled = false;
    let stopViewportSync: () => void = () => undefined;

    void ensurePlatformInitialized()
      .then((adapter) => {
        if (cancelled) return;
        stopViewportSync = adapter.syncViewportCssVars();
        setState(snapshot());
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      stopViewportSync();
    };
  }, []);

  return state;
}

/** Reactive launch hints for onboarding/UI. These values are never auth proof. */
export function usePlatform(): PlatformSnapshot {
  const [state, setState] = useState(snapshot);
  useEffect(() => {
    let cancelled = false;
    void ensurePlatformInitialized()
      .then(() => {
        if (!cancelled) setState(snapshot());
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

/** Uses native back chrome where the host provides it; web/VK remain no-op. */
export function usePlatformBackButton(active: boolean): void {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void ensurePlatformInitialized()
      .then(() => {
        if (!cancelled) setRevision((value) => value + 1);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const adapter = getPlatformAdapter();
    if (!active || !adapter.hasNativeBackButton) return;
    return adapter.showBackButton(() => window.history.back());
  }, [active, revision]);
}

export function useHasNativeHostBackButton(): boolean {
  const [hasBackButton, setHasBackButton] = useState(
    () => getPlatformAdapter().hasNativeBackButton,
  );
  useEffect(() => {
    let cancelled = false;
    void ensurePlatformInitialized()
      .then((adapter) => {
        if (!cancelled) setHasBackButton(adapter.hasNativeBackButton);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return hasBackButton;
}

/** Whether the wrapper already renders top/back chrome, independently of
 * whether its SDK lets Lexibit register a callback for that back control. */
export function useOwnsHostTopBackChrome(): boolean {
  const platform = usePlatform();
  return platform.capabilities.ownsTopBackChrome;
}

/** Protects long-running flows (study sessions, unsaved forms) from host close gestures. */
export function usePlatformClosingConfirmation(active: boolean): void {
  useEffect(() => {
    let cancelled = false;
    let adapter = getPlatformAdapter();
    void ensurePlatformInitialized()
      .then((initializedAdapter) => {
        adapter = initializedAdapter;
        if (!cancelled) adapter.setClosingConfirmation(active);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      adapter.setClosingConfirmation(false);
    };
  }, [active]);
}
